import SideNavbar from '../SideNavbar';
import { useAppSelector } from '../../redux/store';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import {
    initiateGmailAuth,
    handleGmailAuthCallback,
    isGmailAuthorized,
    clearGmailTokens,
    getValidGmailToken,
    refreshGmailToken,
    sendGmailEmail
} from '../../utils/gmailUtils';

const Gmail = () => {
    const navigate = useNavigate();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [authMessage, setAuthMessage] = useState<string>('');
    const [testEmail, setTestEmail] = useState<string>('');
    const [sendingTest, setSendingTest] = useState<boolean>(false);
    const [testEmailResult, setTestEmailResult] = useState<{ success?: boolean; message: string } | null>(null);

    const handleGoBack = () => {
        navigate('/integrations');
    };

    const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);

    // TODO: Replace these selectors with the correct paths from your Redux state
    // You need to provide the correct state paths for these values:
    const isAdmin = false; 
    const isManager = false; 
    const isStaff = false; 

    const currentScreenFromRedux = useAppSelector(state => state.screen.currentScreen);
    // TODO: Import getCurrentScreen function and replace this:
    const currentScreen = currentScreenFromRedux; // Replace with your getCurrentScreen function

    // Check for auth callback from Google OAuth
    const handleAuthCallback = useCallback(async () => {
        const queryParams = new URLSearchParams(window.location.search);
        const code = queryParams.get('code');

        if (code) {
            setLoading(true);
            setAuthMessage('Processing authentication...');

            const success = await handleGmailAuthCallback(code);

            if (success) {
                // Clear URL params to avoid issues with refreshing
                window.history.replaceState({}, document.title, window.location.pathname);
                setIsAuthorized(true);
                setAuthMessage('Successfully connected to Gmail!');
            } else {
                setAuthMessage('Authentication failed. Please try again.');
            }

            setLoading(false);
        }
    }, []);

    // Check auth status and automatically refresh token if needed
    useEffect(() => {
        const validateAndRefreshToken = async () => {
            setLoading(true);

            try {
                // Try to get a valid token (this will automatically try to refresh if needed)
                const token = await getValidGmailToken();

                if (token) {
                    setIsAuthorized(true);
                    setAuthMessage('Connected to Gmail');
                } else {
                    // Check if we have tokens stored but they're invalid
                    const tokensStr = localStorage.getItem('gmailTokens');

                    if (tokensStr) {
                        try {
                            const tokens = JSON.parse(tokensStr);

                            // If we have a refresh token, try to refresh explicitly
                            if (tokens.refresh_token) {
                                setAuthMessage('Refreshing access token...');
                                const refreshSuccess = await refreshGmailToken(tokens.refresh_token);

                                if (refreshSuccess) {
                                    setIsAuthorized(true);
                                    setAuthMessage('Successfully refreshed Gmail token');
                                } else {
                                    // Refresh failed, clear tokens and show connect button
                                    clearGmailTokens();
                                    setIsAuthorized(false);
                                    setAuthMessage('Your Gmail session has expired. Please reconnect.');
                                }
                            } else {
                                // No refresh token, clear and show connect button
                                clearGmailTokens();
                                setIsAuthorized(false);
                                setAuthMessage('');
                            }
                        } catch (error) {
                            // Invalid token format in localStorage, clear it
                            clearGmailTokens();
                            setIsAuthorized(false);
                            setAuthMessage('');
                        }
                    } else {
                        // No tokens found
                        setIsAuthorized(false);
                        setAuthMessage('');
                    }
                }
            } catch (error) {
                console.error('Error validating Gmail tokens:', error);
                setIsAuthorized(false);
                setAuthMessage('Error checking Gmail connection status');
            } finally {
                setLoading(false);
            }
        };

        // First check for OAuth callback parameters
        handleAuthCallback();

        // Then check token validity if no callback is being processed
        const queryParams = new URLSearchParams(window.location.search);
        if (!queryParams.get('code')) {
            validateAndRefreshToken();
        }
    }, [handleAuthCallback]);

    // Handle authentication button click
    const handleAuthClick = () => {
        if (isAuthorized) {
            // Disconnect
            clearGmailTokens();
            setIsAuthorized(false);
            setAuthMessage('Disconnected from Gmail.');
        } else {
            // Connect
            setAuthMessage('Redirecting to Google authentication...');
            initiateGmailAuth();
        }
    };

    // Handle test email sending
    const handleTestEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!testEmail || !testEmail.trim()) {
            setTestEmailResult({
                success: false,
                message: 'Please enter a valid email address'
            });
            return;
        }

        try {
            setSendingTest(true);
            setTestEmailResult(null);

            console.log(`Sending test email`, {
                to: [{ email: testEmail.trim() }],
                subject: 'Gmail Integration Test Email',
                body: `
                <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 600px;">
                    <h2 style="color: #4285F4;">Gmail Integration Test Successful</h2>
                    <p>This email confirms that your Gmail integration with Scholar CRM is working correctly.</p>
                    <p>You can now send emails to your leads and clients directly from the CRM.</p>
                    <hr style="border: 1px solid #eee; margin: 20px 0;" />
                    <p style="color: #666; font-size: 12px;">This is an automated test email sent at ${new Date().toLocaleString()}</p>
                </div>
                `,
                isHtml: true
            });


            const result = await sendGmailEmail({
                to: [{ email: testEmail.trim() }],
                subject: 'Gmail Integration Test Email',
                body: `
                <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 600px;">
                    <h2 style="color: #4285F4;">Gmail Integration Test Successful</h2>
                    <p>This email confirms that your Gmail integration with Scholar CRM is working correctly.</p>
                    <p>You can now send emails to your leads and clients directly from the CRM.</p>
                    <hr style="border: 1px solid #eee; margin: 20px 0;" />
                    <p style="color: #666; font-size: 12px;">This is an automated test email sent at ${new Date().toLocaleString()}</p>
                </div>
                `,
                isHtml: true
            });

            if (result) {
                setTestEmailResult({
                    success: true,
                    message: `Test email successfully sent to ${testEmail}`
                });
            } else {
                setTestEmailResult({
                    success: false,
                    message: 'Failed to send test email. Please try again.'
                });
            }
        } catch (error) {
            console.error('Error sending test email:', error);
            setTestEmailResult({
                success: false,
                message: 'Error sending test email: ' + (error instanceof Error ? error.message : 'Unknown error')
            });
        } finally {
            setSendingTest(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-200">
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>
            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-8 rounded-3xl shadow-md">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            {/* Only show Back button for Admin users */}
                            {currentScreen === 'Admin' && (
                                <button
                                    onClick={handleGoBack}
                                    className="text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    Back to Integrations
                                </button>
                            )}
                            <h1 className="text-2xl font-bold text-gray-900">Gmail Integration</h1>
                            <p className="text-gray-600 mt-2">Connect your Gmail account to send single or bulk emails to your leads and clients directly from the CRM</p>
                        </div>
                        <div className="flex-shrink-0">
                            <img src="/images/desktop/gmailIcon.svg" alt="Gmail" className="w-8 h-8" />
                        </div>
                    </div>

                    {/* Connection Status */}
                    <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="font-medium">Status:</span>
                                {loading ? (
                                    <span className="text-yellow-600 flex items-center">
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Checking...
                                    </span>
                                ) : isAuthorized ? (
                                    <span className="text-green-600 flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                                        </svg>
                                        Connected
                                    </span>
                                ) : (
                                    <span className="text-gray-600">Not Connected</span>
                                )}
                            </div>
                            {authMessage && <p className="text-sm mt-1 text-gray-600">{authMessage}</p>}
                        </div>
                        <button
                            onClick={handleAuthClick}
                            disabled={loading}
                            className={`mt-4 md:mt-0 px-4 py-2 rounded-md text-white font-medium transition-colors ${loading ? 'bg-gray-400 cursor-not-allowed' :
                                isAuthorized ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'
                                }`}
                        >
                            {loading ? 'Processing...' : isAuthorized ? 'Disconnect' : 'Connect Gmail'}
                        </button>
                    </div>

                    {/* Test Email Section - Only show when connected */}
                    {isAuthorized && (
                        <div className="mb-8 border border-gray-200 rounded-lg p-4">
                            <h2 className="text-lg font-semibold mb-3">Test Your Connection</h2>
                            <p className="text-sm text-gray-600 mb-4">
                                Send a test email to verify your Gmail integration is working correctly.
                            </p>

                            <form onSubmit={handleTestEmailSubmit} className="flex flex-col md:flex-row gap-3">
                                <div className="flex-grow">
                                    <label htmlFor="test-email" className="sr-only">Email Address</label>
                                    <input
                                        id="test-email"
                                        type="email"
                                        placeholder="Enter recipient email address"
                                        value={testEmail}
                                        onChange={(e) => setTestEmail(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        disabled={sendingTest}
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={sendingTest || !testEmail}
                                    className={`px-4 py-2 rounded-md text-white font-medium ${sendingTest || !testEmail
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-blue-500 hover:bg-blue-600'
                                        }`}
                                >
                                    {sendingTest ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Sending...
                                        </span>
                                    ) : (
                                        'Send Test Email'
                                    )}
                                </button>
                            </form>

                            {testEmailResult && (
                                <div className={`mt-3 p-3 rounded-md ${testEmailResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                                    }`}>
                                    <div className="flex items-center">
                                        {testEmailResult.success ? (
                                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                                            </svg>
                                        ) : (
                                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                                            </svg>
                                        )}
                                        {testEmailResult.message}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Manage email templates */}
                    <div className="mb-8 border border-gray-200 rounded-lg p-4">
                        <h2 className="text-lg font-semibold mb-3">Manage Email Templates</h2>
                        <p className="text-sm text-gray-600 mb-4">
                            Create and manage reusable email templates for common communications.
                        </p>
                        <button
                            onClick={() => navigate('/integrations/gmail-templates')}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                        >
                            Manage Templates
                        </button>
                    </div>

                    {/* Integration Message Section */}
                    <div className="mt-8 border-t border-gray-200 pt-6">
                        {/* Integration Message Section */}
                        <div className="bg-gray-50 p-4 rounded-lg mb-8 border-l-4 border-blue-500">
                            <h2 className="text-lg font-semibold text-gray-800 mb-3">How Gmail Integration Works</h2>
                            <p className="text-sm text-gray-700">
                                Enjoy a <span className="font-semibold">smooth and flexible</span> email experience with our Gmail integration:
                            </p>
                            <ul className="list-disc ml-5 mt-2 text-sm text-gray-700">
                                <li>Connect once and benefit from streamlined email workflows with fewer clicks</li>
                                <li>Smart authentication that only prompts for Google login when needed</li>
                                <li>Automatic token management ensures uninterrupted access to your Gmail account</li>
                                <li>Securely send personalized emails directly from within your CRM workspace</li>
                            </ul>
                        </div>

                        <h2 className="text-lg font-semibold mb-4">Benefits of Gmail Integration</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <h3 className="font-medium text-blue-800 mb-2">Send Emails Directly</h3>
                                <p className="text-sm text-gray-700">Send personalized emails to leads and clients without leaving the CRM.</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg">
                                <h3 className="font-medium text-green-800 mb-2">Bulk Email Campaigns</h3>
                                <p className="text-sm text-gray-700">Create and send marketing campaigns to multiple recipients at once.</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg">
                                <h3 className="font-medium text-purple-800 mb-2">Email Templates</h3>
                                <p className="text-sm text-gray-700">Save time with reusable email templates for common communications.</p>
                            </div>
                            <div className="bg-yellow-50 p-4 rounded-lg">
                                <h3 className="font-medium text-yellow-800 mb-2">Email Tracking</h3>
                                <p className="text-sm text-gray-700">Track when emails are opened and links are clicked for better follow-ups.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Gmail;